import { useState } from "react"

import { ReviewSample } from "@/components/company/reviews/review-sample"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { WHAT_YOU_CAN_EXPECT_JOURNEY_PHASES } from "@/constants/company/wyce-phases"
import type { ReviewWithData } from "@/helpers/company/review-utils"
import { cn } from "@/lib/utils"

type WhatYouCanExpectSectionProps = {
  sentimentByPhaseId: Record<string, number | null>
  signalCountByPhaseId: Record<string, number>
  reviewsByPhaseId: Record<string, ReviewWithData[]>
}

export function WhatYouCanExpectSection({
  sentimentByPhaseId,
  signalCountByPhaseId,
  reviewsByPhaseId,
}: WhatYouCanExpectSectionProps) {
  const [openPhases, setOpenPhases] = useState<string[]>([
    WHAT_YOU_CAN_EXPECT_JOURNEY_PHASES[0].id,
    WHAT_YOU_CAN_EXPECT_JOURNEY_PHASES[1].id,
    WHAT_YOU_CAN_EXPECT_JOURNEY_PHASES[2].id,
  ])

  return (
    <Accordion
      type="multiple"
      value={openPhases}
      onValueChange={setOpenPhases}
      className="relative"
    >
      {/* Timeline line */}
      <div className="bg-border absolute top-0 bottom-0 left-[15px] w-1 md:left-[19px]" />

      {WHAT_YOU_CAN_EXPECT_JOURNEY_PHASES.map((phase, index) => {
        const isOpen = openPhases.includes(phase.id)
        const reviews = reviewsByPhaseId[phase.id] ?? []
        const signalCount = signalCountByPhaseId[phase.id] ?? 0
        const sentimentRate = sentimentByPhaseId[phase.id]
        let percentage: number | null = null
        if (sentimentRate !== null && sentimentRate !== undefined)
          percentage = Math.round(sentimentRate * 100)
        let sentimentColorClass: string | undefined
        if (percentage !== null)
          sentimentColorClass =
            percentage >= 70 ? "text-good-foreground" : "text-red-700"
        const number = String(index + 1).padStart(2, "0")

        return (
          <AccordionItem
            key={phase.id}
            value={phase.id}
            className="relative mb-4 border-none pl-12 md:pl-16"
          >
            {/* Timeline marker */}
            <div
              className={cn(
                "border-border absolute top-3 left-0 flex h-8 w-8 items-center justify-center border-2 md:h-10 md:w-10",
                isOpen ? "bg-brand" : "bg-background"
              )}
            >
              <span className="wb-data font-bold">{number}</span>
            </div>

            <AccordionTrigger
              // hideIcon
              className="hover:border-b-0 hover:no-underline [&[data-state=open]_.chevron-icon]:rotate-180"
            >
              <div className="flex w-full items-center justify-between pr-4">
                <div>
                  <div className="wb-h6 flex-1 text-left">{phase.label}</div>
                  <div className="text-muted-foreground">{phase.question}</div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="wb-data">
                    {signalCount}{" "}
                    {signalCount === 1 ? "data point" : "data points"}
                  </span>
                  {percentage !== null && (
                    <span
                      className={cn(
                        "font-mono font-black",
                        sentimentColorClass
                      )}
                    >
                      {percentage}% positive
                    </span>
                  )}
                </div>
              </div>
            </AccordionTrigger>

            <AccordionContent>
              {reviews.length > 0 ? (
                <ReviewSample
                  reviews={reviews}
                  signals={phase.signals}
                  layout="stacked"
                  shouldHideTags
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  No reviews found for this phase.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}
